import React from 'react';
import { localIndicator, programme } from './GSMonitorList';
import { COMMON_FILTER_TEXT } from '../../lang';
import {
  Alert,
  type DropDownSelectOption,
  FilterPanel,
  type FilterPanelProps,
  Loading,
  type OptionGroups,
} from '../../../../components';
import { useTableFilterData } from '../../../Application/graphql';
import { convertEntryYearMap } from '../../../Application/components';
import { getFilterPriority } from '../../DRCAssessment/components/DSRCFilter';

type GSTableFilterProps = Omit<FilterPanelProps, 'filterOptionGroups'> & {
  optionGroups?: OptionGroups[];
  apiType: GS_FILTER_API_TYPE;
};

export const enum GS_FILTER_API_TYPE {
  FailedApplicationListFilter = 'FailedApplicationListFilter',
  nominationsPaginated = 'nominationsPaginated',
  gsDecisionPaginated = 'gsDecisionPaginated',
  offerList = 'offerList',
}
const enum CustomizedField {
  department = 'department',
  entryYear = 'entryYear',
  programme = 'programme',
  isLocal = 'isLocal',
  applicationNo = 'applicationNo',
}

const mappings: Record<GS_FILTER_API_TYPE, Record<CustomizedField, string>> = {
  [GS_FILTER_API_TYPE.FailedApplicationListFilter]: {
    [CustomizedField.department]: 'programmeChoice.department', // the `department` in topper level is not applicable
    [CustomizedField.entryYear]: 'programmeChoice.entryYear',
    [CustomizedField.programme]: 'programmeChoice.programme',
    [CustomizedField.isLocal]: 'applicant.identity.isLocal',
    [CustomizedField.applicationNo]: 'applicationNo',
  },
  [GS_FILTER_API_TYPE.nominationsPaginated]: {
    [CustomizedField.department]: 'application.programmeChoice.department', // the `department` in topper level is not applicable
    [CustomizedField.entryYear]: 'application.programmeChoice.entryYear',
    [CustomizedField.programme]: 'application.programmeChoice.programme',
    [CustomizedField.isLocal]: 'application.applicant.identity.isLocal',
    [CustomizedField.applicationNo]: 'application.applicationNo',
  },
  [GS_FILTER_API_TYPE.gsDecisionPaginated]: {
    [CustomizedField.department]: 'department',
    [CustomizedField.entryYear]: 'entryYear',
    [CustomizedField.programme]: 'programme',
    [CustomizedField.isLocal]: 'isLocal',
    [CustomizedField.applicationNo]: 'applicationNo',
  },
  [GS_FILTER_API_TYPE.offerList]: {
    [CustomizedField.department]: 'department',
    [CustomizedField.entryYear]: 'entryYear',
    [CustomizedField.programme]: 'programme',
    [CustomizedField.isLocal]: 'isLocal',
    [CustomizedField.applicationNo]: 'applicationNo',
  },
};

export const GSTableFilter: React.FC<GSTableFilterProps> = (
  props: GSTableFilterProps,
) => {
  const {
    initialAppliedFilter,
    optionGroups: extraOptionGroups,
    setFilter: setParentFilter,
    apiType,
  } = props;

  // get entryYear,department from backend setting
  const { departments, entryYears, loading, error } = useTableFilterData();

  const departmentOptions: DropDownSelectOption[] = (departments as any[]).map(
    department => ({
      value: department.value,
      label: department.label,
    }),
  );

  const entryYearsOptions: DropDownSelectOption[] = (entryYears as any[]).map(
    entryYear => ({
      value: entryYear,
      label: convertEntryYearMap[entryYear] ?? entryYear,
    }),
  );
  const enableFuzzyApplicantName =
    apiType === GS_FILTER_API_TYPE.offerList ||
    apiType === GS_FILTER_API_TYPE.gsDecisionPaginated;

  const enableFuzzyEmail = apiType === GS_FILTER_API_TYPE.gsDecisionPaginated;

  const filterOptionGroups: OptionGroups[] = [
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.DEPT,
      options: departmentOptions,
      type: 'select',
      fieldName: mappings[apiType][CustomizedField.department],
      fieldType: 'string',
      displayLabel: false, // to display the abb
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.ENTRY_YR,
      options: entryYearsOptions,
      type: 'select',
      fieldName: mappings[apiType][CustomizedField.entryYear],
      fieldType: 'string',
      displayLabel: false,
    },

    // {
    //   title: COMMON_FILTER_TEXT.FILTER_CATEGORY.CURRENT_STATUS,
    //   options: currentStatus,
    //   type: 'select',
    //   fieldName: 'applicationStatus',
    //   fieldType: 'string',
    //   displayLabel: true,
    // },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.ADM_PROG,
      options: programme,
      type: 'select',
      fieldName: mappings[apiType][CustomizedField.programme],
      fieldType: 'string',
      displayLabel: true,
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.LOCAL_INDICATOR,
      options: localIndicator,
      type: 'select',
      fieldName: mappings[apiType][CustomizedField.isLocal],
      fieldType: 'boolean',
      displayLabel: true,
    },
    ...(extraOptionGroups ?? []),
    // Only the offerList API supports fuzzy query of full name
    ...(enableFuzzyApplicantName
      ? [
          {
            title: COMMON_FILTER_TEXT.FILTER_CATEGORY.APPLICATION_NAME,
            fieldName: 'applicantName',
            type: 'text' as 'select' | 'text',
            isFuzzy: true,
          },
        ]
      : []),
    ...(enableFuzzyEmail
      ? [
          {
            title: COMMON_FILTER_TEXT.FILTER_CATEGORY.EMAIL,
            fieldName: 'email',
            type: 'text' as 'select' | 'text',
            isFuzzy: true,
          },
        ]
      : []),
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.APPLICATION_NO,
      fieldName: mappings[apiType][CustomizedField.applicationNo],
      type: 'text',
      isFuzzy: true,
    },
  ];

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  filterOptionGroups.sort((a: OptionGroups, b: OptionGroups) => {
    return getFilterPriority(a.type) - getFilterPriority(b.type);
  });

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <FilterPanel
          setFilter={setParentFilter}
          initialAppliedFilter={initialAppliedFilter}
          filterOptionGroups={filterOptionGroups}
        />
      )}
    </>
  );
};
